import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
  Table,
} from "@themesberg/react-bootstrap";
import { Link } from "react-router-dom";
import { useCookies } from "react-cookie";
import { toast } from "react-toastify";
import {
  getRewardCalcSetting,
  updateRewardCalcSetting,
} from "../../controller/api/rewardCalcJob";
import { Routes } from "../../routes";

const GAME_OPTIONS = [
  { id: "keno", name: "Keno" },
  { id: "power", name: "Power 6/55" },
  { id: "mega", name: "Mega 6/45" },
  { id: "max3d", name: "Max3D" },
  { id: "max3dpro", name: "Max3D Pro" },
  { id: "lotto535", name: "Lotto 5/35" },
  { id: "classic", name: "Kiến thiết" },
  { id: "loto", name: "Loto" },
];

const DEFAULT_AUTO_GAMES = {
  keno: false,
  mega: false,
  power: false,
  max3d: false,
  max3dpro: false,
  lotto535: false,
  classic: false,
  loto: false,
};

const DEFAULT_AUTO_PAY_GAMES = {
  keno: false,
  mega: false,
  power: false,
  max3d: false,
  max3dpro: false,
  lotto535: false,
  classic: false,
  loto: false,
};

export default () => {
  const [cookies] = useCookies(["lottmekong"]);
  const token = cookies.lottmekong;

  const [autoGames, setAutoGames] = useState(DEFAULT_AUTO_GAMES);
  const [autoPayGames, setAutoPayGames] = useState(DEFAULT_AUTO_PAY_GAMES);
  const [concurrency, setConcurrency] = useState(2);
  const [savingSetting, setSavingSetting] = useState(false);

  useEffect(() => {
    document.title = "Tự động tính thưởng";
  }, []);

  const applyConfig = useCallback((config) => {
    setAutoGames({ ...DEFAULT_AUTO_GAMES, ...config.autoGames });
    setAutoPayGames({ ...DEFAULT_AUTO_PAY_GAMES, ...config.autoPayGames });
    setConcurrency(config.concurrency);
  }, []);

  const fetchSetting = useCallback(async () => {
    const config = await getRewardCalcSetting(token);
    if (config) {
      applyConfig(config);
    }
  }, [token, applyConfig]);

  useEffect(() => {
    fetchSetting();
  }, [fetchSetting]);

  const saveSetting = async (
    nextAutoGames,
    nextAutoPayGames,
    nextConcurrency
  ) => {
    setSavingSetting(true);
    try {
      const config = await updateRewardCalcSetting(
        token,
        nextAutoGames,
        nextAutoPayGames,
        Number(nextConcurrency)
      );
      if (config) {
        applyConfig(config);
        toast.success("Đã lưu cấu hình tính thưởng");
      } else {
        toast.error("Không thể lưu cấu hình tính thưởng");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Không thể lưu cấu hình tính thưởng"
      );
    } finally {
      setSavingSetting(false);
    }
  };

  const onToggleAuto = (gameId) => {
    saveSetting(
      { ...autoGames, [gameId]: !autoGames[gameId] },
      autoPayGames,
      concurrency
    );
  };

  const onToggleAutoPay = (gameId) => {
    saveSetting(
      autoGames,
      { ...autoPayGames, [gameId]: !autoPayGames[gameId] },
      concurrency
    );
  };

  const onSaveConcurrency = () => {
    saveSetting(autoGames, autoPayGames, concurrency);
  };

  return (
    <Card border="light" className="shadow-sm mb-4">
      <Card.Header className="d-flex align-items-center justify-content-between">
        <div>
          <h5 className="mb-0">Tự động tính thưởng</h5>
          <small className="text-muted">
            Tự động tính sẽ tạo job khi có kết quả; tự động trả sẽ cộng tiền vào
            ví thưởng của vé trúng.
          </small>
        </div>
        <Button
          as={Link}
          to={Routes.VietlottRewardCalcJob.path}
          variant="outline-secondary"
          size="sm"
        >
          Xem danh sách job
        </Button>
      </Card.Header>
      <Card.Body>
        <Table responsive size="sm" className="align-items-center table-flush">
          <thead className="thead-light">
            <tr>
              <th scope="col">Game</th>
              <th scope="col">Tự động tính</th>
              <th scope="col">Tự động trả</th>
            </tr>
          </thead>
          <tbody>
            {GAME_OPTIONS.map((game) => (
              <tr key={`reward-calc-setting-${game.id}`}>
                <td className="fw-bold">{game.name}</td>
                <td>
                  <Form.Check
                    type="switch"
                    id={`reward-calc-auto-${game.id}`}
                    label={autoGames[game.id] ? "Bật" : "Tắt"}
                    checked={autoGames[game.id]}
                    onChange={() => onToggleAuto(game.id)}
                    disabled={savingSetting}
                  />
                </td>
                <td>
                  <Form.Check
                    type="switch"
                    id={`reward-pay-auto-${game.id}`}
                    label={autoPayGames[game.id] ? "Bật" : "Tắt"}
                    checked={autoPayGames[game.id]}
                    onChange={() => onToggleAutoPay(game.id)}
                    disabled={savingSetting}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Row className="mt-3">
          <Col md={4}>
            <Form.Label className="small mb-1">
              Số job chạy đồng thời (tất cả game)
            </Form.Label>
            <InputGroup size="sm">
              <Form.Control
                type="number"
                min={1}
                max={10}
                value={concurrency}
                onChange={(event) => setConcurrency(event.target.value)}
                disabled={savingSetting}
              />
              <Button
                variant="outline-primary"
                onClick={onSaveConcurrency}
                disabled={savingSetting}
              >
                {savingSetting ? <Spinner animation="border" size="sm" /> : "Lưu"}
              </Button>
            </InputGroup>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};
